import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Skeleton,
  Tooltip,
  IconButton,
  Checkbox,
} from '@mui/material';
import { PlusIcon, XIcon, GitMergeIcon } from '@phosphor-icons/react';
import { useDispatch, useSelector } from 'react-redux';
import theme from '../../../theme';
import {
  getPromptVersionHistoryApi,
  useSystemPromptApi,
} from '../../../utils/apis';
import { useDashboard } from '../../../hooks/useDashboard';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAlert } from '../../../hooks/useAlert';
import MessagePopup from '../../common/MessagePopup';
import AddLabelToVersionHistoryModal from '../../modals/AddLabelToVersionHistoryModal';
import EditVersionHistory from '../../modals/EditVersionHistory';
import { routes } from '../../../constants/routes';
import { useNavigate } from 'react-router';
import { RootState, AppDispatch } from '../../../utils/redux';
import {
  toggleVersionSelection,
  clearVersionSelection,
} from '../../../utils/redux/slices/versionComparisonSlice';

interface VersionHistorySidebarProps {
  onClose?: () => void;
}

// Memoized version item component to prevent unnecessary re-renders
const VersionItem = React.memo<{
  version: any;
  index: number;
  isLast: boolean;
  isCurrentVersion: boolean;
  updatingVersionId: string | null;
  isSelected: boolean;
  onUseSystemPrompt: (versionId: string) => void;
  onOpenLabelModal: (versionId: string) => void;
  onOpenEditModal: (version: any) => void;
  onToggleSelection: (versionId: string) => void;
}>(
  ({
    version,
    // isLast,
    isCurrentVersion,
    updatingVersionId,
    isSelected,
    onUseSystemPrompt,
    onOpenLabelModal,
    onOpenEditModal,
    onToggleSelection,
  }) => {
    // Memoize time formatting to avoid recalculation
    const timeAgo = useMemo(() => {
      const now = new Date();
      const versionDate = new Date(version.updatedAt);

      if (isNaN(versionDate.getTime())) {
        return 'Invalid date';
      }

      const diffInMs = now.getTime() - versionDate.getTime();
      if (diffInMs < 0) return 'just now';

      const diffInMinutes = Math.floor(diffInMs / 60000);
      const diffInHours = Math.floor(diffInMinutes / 60);
      const diffInDays = Math.floor(diffInHours / 24);

      if (diffInMinutes < 1) return 'just now';
      if (diffInHours < 1)
        return diffInMinutes === 1
          ? '1 minute ago'
          : `${diffInMinutes} minutes ago`;
      if (diffInDays < 1)
        return diffInHours === 1 ? '1 hour ago' : `${diffInHours} hours ago`;
      if (diffInDays < 30)
        return diffInDays === 1 ? '1 day ago' : `${diffInDays} days ago`;

      const diffInMonths = Math.floor(diffInDays / 30);
      return diffInMonths === 1 ? '1 month ago' : `${diffInMonths} months ago`;
    }, [version.updatedAt]);

    // Memoize event handlers
    const handleUseSystemPrompt = useCallback(() => {
      onUseSystemPrompt(version._id);
    }, [version._id, onUseSystemPrompt]);

    const handleOpenLabelModal = useCallback(() => {
      onOpenLabelModal(version._id);
    }, [version._id, onOpenLabelModal]);

    const handleOpenEditModal = useCallback(() => {
      onOpenEditModal(version);
    }, [version, onOpenEditModal]);

    const handleToggleSelection = useCallback(() => {
      onToggleSelection(version._id);
    }, [version._id, onToggleSelection]);

    return (
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          p: 2,
          mb: 2,
          border: `1px solid ${theme.palette.lightGray.main}`,
          '&:hover': {
            backgroundColor: theme.palette.custom.sidebarIconBg,
            cursor: 'pointer',
          },
        }}
        data-version-id={version._id}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {/* Version Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Checkbox */}
              <Tooltip title="Select version for comparison">
                <Checkbox
                  size="small"
                  checked={isSelected}
                  onChange={handleToggleSelection}
                  sx={{
                    p: 0,
                    mt: '2px',
                    '& .MuiSvgIcon-root': {
                      fontSize: '16px',
                    },
                  }}
                />
              </Tooltip>
              <Box sx={{ flex: 1 }}>
                {/* Version Header */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  >
                    Version {version?.version}
                  </Typography>
                  {isCurrentVersion && (
                    <Chip
                      label="In Use"
                      size="small"
                      sx={{
                        height: '18px',
                        fontSize: '10px',
                        fontWeight: 500,
                        backgroundColor: theme.palette.lightGray.main,
                        color: theme.palette.text.primary,
                        '& .MuiChip-label': {
                          px: 1,
                          fontStyle: 'italic',
                        },
                      }}
                    />
                  )}
                </Box>

                {/* Timestamp */}
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '12px',
                    color: theme.palette.text.secondary,
                    mb: 1,
                  }}
                >
                  {timeAgo}
                </Typography>
              </Box>
            </Box>

            {/* Commit Message */}
            {version.commitMessage && (
              <Tooltip title={version.commitMessage}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: theme.palette.text.secondary,
                    fontStyle: 'italic',
                    mb: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  "{version.commitMessage}"
                </Typography>
              </Tooltip>
            )}

            {/* Labels */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                flexWrap: 'wrap',
              }}
            >
              {version.label?.map((label: string, labelIndex: number) => (
                <Chip
                  key={labelIndex}
                  label={label}
                  size="small"
                  sx={{
                    height: '20px',
                    fontSize: '10px',
                    padding: '2px 12px',
                    border: `1px solid ${theme.palette.lightGray.main}`,
                    color: theme.palette.primary.main,
                    backgroundColor: theme.palette.custom.sidebarIconBg,
                    '& .MuiChip-label': {
                      px: 1,
                    },
                  }}
                />
              ))}
              <Tooltip title="Add Label">
                <IconButton
                  size="small"
                  onClick={handleOpenLabelModal}
                  sx={{
                    width: '20px',
                    height: '20px',
                    color: theme.palette.text.secondary,
                  }}
                >
                  <PlusIcon size={12} />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleOpenEditModal}
                sx={{
                  textTransform: 'none',
                  fontSize: '12px',
                  height: '28px',
                  borderRadius: '6px',
                  borderColor: theme.palette.lightGray.main,
                  color: theme.palette.text.primary,
                  flex: 1,
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={updatingVersionId === version._id}
                onClick={handleUseSystemPrompt}
                sx={{
                  textTransform: 'none',
                  fontSize: '12px',
                  height: '28px',
                  borderRadius: '6px',
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  boxShadow: 'none',
                  flex: 1,
                  '&:hover': {
                    boxShadow: 'none',
                    borderColor: theme.palette.primary.main,
                    backgroundColor: 'transparent',
                  },
                  '&:disabled': {
                    backgroundColor: theme.palette.lightGray.main,
                    color: theme.palette.text.secondary,
                  },
                }}
              >
                {updatingVersionId === version._id ? 'Updating...' : 'Use'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
);

VersionItem.displayName = 'VersionItem';

const VersionHistorySidebar: React.FC<VersionHistorySidebarProps> = ({
  onClose,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();
  const { selectedAssistant } = useDashboard();
  const { alert, setAlert, showAlert } = useAlert();
  const [promptVersionsHistory, setPromptVersionHistory] = useState<any>([]);
  const [isLabelModalOpen, setIsLabelModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [selectedVersionId, setSelectedVersionId] = useState<string>('');
  const [selectedVersion, setSelectedVersion] = useState<any>({});
  const [updatingVersionId, setUpdatingVersionId] = useState<string | null>(
    null
  );

  // Get selected versions from Redux store
  const selectedVersions = useSelector(
    (state: RootState) =>
      state.versionComparison.selectedVersions[selectedAssistant?._id || ''] ||
      []
  );

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Memoize date calculations to prevent recreation on every render
  const dateRange = useMemo(() => {
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - 30);
    const toDate = new Date();

    const formatDate = (date: Date) => date.toISOString().split('T')[0];

    return {
      fromDate: formatDate(fromDate),
      toDate: formatDate(toDate),
    };
  }, []);

  const { data: promptData, isLoading: promptLoading } = useQuery({
    queryKey: [
      'get-version-history-sidebar',
      selectedAssistant?._id,
      dateRange.fromDate,
      dateRange.toDate,
    ],
    queryFn: () =>
      getPromptVersionHistoryApi({
        agentId: selectedAssistant?._id || '',
        fromDate: dateRange.fromDate,
        toDate: dateRange.toDate,
      }),
    refetchOnMount: false,
    staleTime: 5 * 60 * 1000,
    enabled: !!selectedAssistant,
  });

  useEffect(() => {
    if (promptData) {
      setPromptVersionHistory(promptData?.data?.data || []);
    }
  }, [promptData]);

  // Memoize scroll function
  const scrollToCurrentVersion = useCallback(() => {
    if (!scrollContainerRef.current || !selectedAssistant?.file?.versionId) {
      return;
    }

    const currentVersionElement = scrollContainerRef.current.querySelector(
      `[data-version-id="${selectedAssistant.file.versionId}"]`
    );

    if (currentVersionElement) {
      currentVersionElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [selectedAssistant?.file?.versionId]);

  const { mutateAsync: systemPromptAsync } = useMutation({
    mutationFn: useSystemPromptApi,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['get-version-history-sidebar'],
      });
      queryClient.invalidateQueries({
        queryKey: ['get-version-history'],
      });
      queryClient.refetchQueries({
        queryKey: ['agents'],
      });
      showAlert('Prompt updated successfully', 'success');
      setUpdatingVersionId(null);

      setTimeout(() => {
        scrollToCurrentVersion();
      }, 500);
    },
    onError: (error: any) => {
      showAlert(
        error?.response?.data?.message || 'Failed to use prompt',
        'error'
      );
      setUpdatingVersionId(null);
    },
  });

  // Memoize event handlers to prevent child re-renders
  const handleUseSystemPrompt = useCallback(
    (versionId: string) => {
      setUpdatingVersionId(versionId);
      systemPromptAsync({
        versionId: versionId,
        agentId: selectedAssistant?._id || '',
      });
    },
    [systemPromptAsync, selectedAssistant?._id]
  );

  const handleOpenLabelModal = useCallback((versionId: string) => {
    setSelectedVersionId(versionId);
    setIsLabelModalOpen(true);
  }, []);

  const handleCloseLabelModal = useCallback(() => {
    setIsLabelModalOpen(false);
  }, []);

  const handleOpenEditModal = useCallback((version: any) => {
    setSelectedVersion(version);
    setIsEditModalOpen(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedVersion({});
  }, []);

  const handleToggleSelection = useCallback(
    (versionId: string) => {
      if (selectedAssistant?._id) {
        dispatch(
          toggleVersionSelection({
            agentId: selectedAssistant._id,
            versionId: versionId,
          })
        );
      }
    },
    [dispatch, selectedAssistant?._id]
  );

  const handleCompareVersions = useCallback(() => {
    if (selectedVersions.length === 2 && selectedAssistant?._id) {
      const route = routes.promptCompare
        .replace(':agentId', selectedAssistant._id)
        .replace(':versionId1', selectedVersions[0])
        .replace(':versionId2', selectedVersions[1]);
      navigate(route);
    }
  }, [selectedVersions, navigate, selectedAssistant?._id]);

  const handleClearSelection = useCallback(() => {
    if (selectedAssistant?._id) {
      dispatch(
        clearVersionSelection({
          agentId: selectedAssistant._id,
        })
      );
    }
  }, [dispatch, selectedAssistant?._id]);

  // Memoize current version ID for comparison
  const currentVersionId = useMemo(
    () => selectedAssistant?.file?.versionId,
    [selectedAssistant?.file?.versionId]
  );

  // Memoize container styles
  const containerStyle = useMemo(
    () => ({
      width: '320px',
      height: '100%',
      backgroundColor: '#FFFFFF',
      borderLeft: `1px solid ${theme.palette.lightGray.main}`,
      display: 'flex',
      flexDirection: 'column' as const,
    }),
    []
  );

  const scrollContainerStyle = useMemo(
    () => ({
      flex: 1,
      overflowY: 'auto' as const,
      p: 2,
      '&::-webkit-scrollbar': { display: 'none' },
      msOverflowStyle: 'none',
      scrollbarWidth: 'none',
    }),
    []
  );

  if (promptLoading) {
    return (
      <Box sx={containerStyle}>
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${theme.palette.lightGray.main}`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Skeleton variant="text" width={120} height={24} />
            {onClose && (
              <IconButton size="small" onClick={onClose}>
                <XIcon size={16} />
              </IconButton>
            )}
          </Box>
        </Box>
        <Box sx={{ flex: 1, p: 2 }}>
          {Array.from({ length: 5 }).map((_, index) => (
            <Box key={index} sx={{ mb: 3 }}>
              <Skeleton variant="text" width="60%" height={20} />
              <Skeleton
                variant="text"
                width="40%"
                height={16}
                sx={{ mt: 0.5 }}
              />
              <Skeleton
                variant="text"
                width="80%"
                height={14}
                sx={{ mt: 0.5 }}
              />
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Skeleton
                  variant="rectangular"
                  width={60}
                  height={24}
                  sx={{ borderRadius: '12px' }}
                />
                <Skeleton
                  variant="rectangular"
                  width={20}
                  height={20}
                  sx={{ borderRadius: '50%' }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={containerStyle}>
      {/* Header */}
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: selectedVersions.length === 2 ? 1 : 0,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: '16px',
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            Prompt Versions
          </Typography>
          {onClose && (
            <IconButton size="small" onClick={onClose}>
              <XIcon size={16} />
            </IconButton>
          )}
        </Box>
        {selectedVersions.length === 0 && (
          <Typography
            variant="body2"
            sx={{
              fontSize: '12px',
              color: theme.palette.text.secondary,
              fontStyle: 'italic',
            }}
          >
            (Click the checkboxes to compare versions)
          </Typography>
        )}

        {/* Compare Button - Show when exactly 2 versions are selected */}
        {selectedVersions.length === 2 && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleCompareVersions}
              startIcon={<GitMergeIcon size={14} />}
              sx={{
                textTransform: 'none',
                boxShadow: 'none',
                fontSize: '12px',
                height: '32px',
                borderRadius: '6px',
                backgroundColor: theme.palette.primary.main,
                flex: 1,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              }}
            >
              Compare Versions
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearSelection}
              sx={{
                textTransform: 'none',
                boxShadow: 'none',
                fontSize: '12px',
                height: '32px',
                borderRadius: '6px',
                borderColor: theme.palette.lightGray.main,
                color: theme.palette.text.secondary,
                minWidth: 'auto',
                px: 2,
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: 'transparent',
                },
              }}
            >
              Clear
            </Button>
          </Box>
        )}

        {/* Selection indicator */}
        {selectedVersions.length > 0 && (
          <Typography
            variant="body2"
            sx={{
              fontSize: '11px',
              color: theme.palette.text.secondary,
              mt: 1,
            }}
          >
            {selectedVersions.length === 1
              ? '1 version selected (select 1 more to compare)'
              : `${selectedVersions.length} versions selected`}
          </Typography>
        )}
      </Box>

      {/* Version List */}
      <Box ref={scrollContainerRef} sx={scrollContainerStyle}>
        {promptVersionsHistory?.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary }}
            >
              No version history available
            </Typography>
          </Box>
        ) : (
          promptVersionsHistory?.map((version: any, index: number) => (
            <VersionItem
              key={version?._id}
              version={version}
              index={index}
              isLast={index === promptVersionsHistory.length - 1}
              isCurrentVersion={version._id === currentVersionId}
              isSelected={selectedVersions.includes(version._id)}
              updatingVersionId={updatingVersionId}
              onUseSystemPrompt={handleUseSystemPrompt}
              onOpenLabelModal={handleOpenLabelModal}
              onOpenEditModal={handleOpenEditModal}
              onToggleSelection={handleToggleSelection}
            />
          ))
        )}
      </Box>

      <MessagePopup alert={alert} setAlert={setAlert} />
      <AddLabelToVersionHistoryModal
        open={isLabelModalOpen}
        onClose={handleCloseLabelModal}
        versionId={selectedVersionId}
        selectedView="single"
      />
      <EditVersionHistory
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        version={selectedVersion}
      />
    </Box>
  );
};

export default React.memo(VersionHistorySidebar);
